import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Mic, Layers, IndianRupee, ArrowRight } from 'lucide-react';
import { BRAND } from '../lib/constants';

const BADGES = [
    { icon: Sparkles, label: 'AI-Powered' },
    { icon: Layers, label: '5 Rounds' },
    { icon: Mic, label: 'Voice-Enabled' },
    { icon: IndianRupee, label: 'Salary Practice' },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-white">
            {/* Top strip */}
            <header className="border-b border-[#E5E7EB] bg-white">
                <div className="page-container flex h-16 items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={BRAND.logoUrl} alt="ExcellenC" className="h-9" />
                        <div className="leading-none">
                            <div className="brand-wordmark text-[18px] leading-none">{BRAND.name}</div>
                            <div className="brand-wordmark-sub text-[11px] mt-0.5">{BRAND.subtitle}</div>
                        </div>
                    </div>
                    <Link
                        to="/login"
                        data-testid="landing-login-link"
                        className="brand-btn-outline"
                    >
                        Sign In
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-[#F5F7FE] to-[#FFEFEF]" />
                <div className="page-container py-16 md:py-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="max-w-3xl"
                    >
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs font-semibold text-[#1C3A94]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#E52C2C]" />
                            LIVE FOR EXCELLENC LEARNERS
                        </div>
                        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-[#111827]">
                            <span className="brand-gradient-text">Ace</span> your next{' '}
                            <br className="hidden sm:block" />
                            interview with{' '}
                            <span className="brand-gradient-text">PlacementPilot</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base md:text-lg text-[#6B7280] leading-relaxed">
                            {BRAND.tagline}. Get instant AI evaluation, voice-based practice, and a
                            realistic 5-round journey — including salary negotiation.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3">
                            {BADGES.map((b) => (
                                <div
                                    key={b.label}
                                    className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-3.5 py-1.5 text-xs font-semibold text-[#1C3A94] shadow-sm"
                                >
                                    <b.icon className="h-3.5 w-3.5" />
                                    {b.label}
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link
                                to="/login"
                                data-testid="landing-get-started-btn"
                                className="brand-btn-primary group gap-2"
                            >
                                Get Started
                                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                            </Link>
                            <Link to="/login" className="brand-btn-outline">
                                I already have an account
                            </Link>
                        </div>
                    </motion.div>

                    {/* Feature cards */}
                    <div className="mt-16 grid gap-5 md:grid-cols-3">
                        {[
                            {
                                title: '5 Realistic Rounds',
                                body: 'Technical → Case Study → Behavioral → Final Review → Salary Negotiation. Each round tailored to your course.',
                                accent: '#1C3A94',
                            },
                            {
                                title: 'AI Evaluation',
                                body: 'Every answer scored on a 1-10 scale with feedback, strengths, and model answers to learn from.',
                                accent: '#E52C2C',
                            },
                            {
                                title: 'Voice & Text',
                                body: 'Practice speaking your answers out loud with browser-based speech recognition and playback.',
                                accent: '#10B981',
                            },
                        ].map((c) => (
                            <motion.div
                                key={c.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4 }}
                                className="brand-card p-6"
                            >
                                <div
                                    className="mb-4 h-1 w-10 rounded-full"
                                    style={{ background: c.accent }}
                                />
                                <div className="text-lg font-bold text-[#111827]">{c.title}</div>
                                <div className="mt-2 text-sm text-[#6B7280] leading-relaxed">
                                    {c.body}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <footer className="border-t border-[#E5E7EB] bg-[#F9FAFB]">
                <div className="page-container py-6 text-center text-xs text-[#6B7280]">
                    © {new Date().getFullYear()} ExcellenC — PlacementPilot
                </div>
            </footer>
        </div>
    );
}
